import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  Label,
} from "@repo/ui";
import { Loader2, ShieldCheck } from "lucide-react";

import { forceChangePassword } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/auth-provider";
import { SecureInput } from "@/components/forms";

export const Route = createFileRoute("/_auth/change-password")({
  component: ChangePasswordPage,
});

interface ChangePasswordUser {
  id: string;
  full_name: string;
  initials: string;
  role: string;
}

function ChangePasswordPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [user, setUser] = useState<ChangePasswordUser | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const userData = sessionStorage.getItem("changePasswordUser");
    if (!userData) {
      navigate({ to: "/select-user" });
      return;
    }

    const parsed = JSON.parse(userData);
    if (!parsed.id) {
      sessionStorage.removeItem("changePasswordUser");
      navigate({ to: "/select-user" });
      return;
    }

    setUser(parsed);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!user) return;

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (newPassword.length < 4 || newPassword.length > 16) {
      setError("Password must be 4-16 characters");
      return;
    }

    setIsLoading(true);

    try {
      await forceChangePassword({ newPassword });

      // The server sets the active-user cookie on success.
      // Set the active user in sessionStorage and refresh auth context
      // so the _app layout guard sees the active user before we navigate.
      sessionStorage.setItem("activeUser", JSON.stringify(user));
      sessionStorage.removeItem("changePasswordUser");
      await auth.refetch();
      navigate({ to: "/dashboard" });
    } catch (err) {
      const apiErr = err as { statusCode?: number; message?: string };
      setError(apiErr.message || "Failed to change password");
      setNewPassword("");
      setConfirmPassword("");
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {user.initials}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -right-1 -bottom-1 rounded-full bg-yellow-500 p-1.5">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>

          <CardTitle className="text-2xl">Change Your Password</CardTitle>
          <CardDescription>
            For security reasons, you must change your temporary password before
            continuing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="new-staff-pin">New Password</Label>
              <SecureInput
                id="new-staff-pin"
                value={newPassword}
                onChange={setNewPassword}
                maxLength={16}
                disabled={isLoading}
                autoFocus
              />
              <p className="text-muted-foreground text-xs">
                Must be 4-16 characters and different from current password
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-staff-pin">Confirm New Password</Label>
              <SecureInput
                id="confirm-staff-pin"
                value={confirmPassword}
                onChange={setConfirmPassword}
                maxLength={16}
                disabled={isLoading}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={
                isLoading ||
                newPassword.length < 4 ||
                confirmPassword.length < 4
              }
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Changing Password...
                </>
              ) : (
                "Change Password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
